import { useRef, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Camera, Loader2, CheckCircle2 } from 'lucide-react';

// Optional AI OCR: capture/upload a photo of the hour meter, auto-fill the reading,
// and hand the captured image back to the parent so it can be stored as photo-proof.
export function HourMeterScanner({ onDetected, testId = 'scan-hour-meter' }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please use a JPEG, PNG or WEBP image');
      return;
    }
    setLoading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await api.post('/ocr/hour-meter', { image_base64: base64, mime_type: file.type });
      if (res.data.detected && res.data.value != null) {
        setPreview(base64);
        onDetected(res.data.value, base64);
        toast.success(`Detected ${res.data.value.toLocaleString()} hrs — verify & photo saved with this reading`);
      } else {
        toast.error("Couldn't read the meter clearly. Please enter it manually.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {preview && (
        <div className="flex items-center gap-1 text-[10px] text-emerald-600" data-testid={`${testId}-attached`}>
          <img src={preview} alt="meter" className="h-6 w-6 rounded object-cover border border-emerald-200" />
          <CheckCircle2 size={12} /> Photo
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid={`${testId}-input`} />
      <Button type="button" variant="outline" size="sm" disabled={loading} onClick={() => inputRef.current?.click()} className="gap-1.5 border-slate-300 text-slate-600 hover:text-slate-900" data-testid={testId} title="Scan hour meter from a photo (optional)">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
        {loading ? 'Reading...' : preview ? 'Re-scan' : 'Scan'}
      </Button>
    </div>
  );
}
